/**
 * Microsoft Entra External ID Authentication Configuration
 *
 * This file contains the MSAL configuration for Microsoft Entra External ID authentication.
 * Configuration is resolved from VITE env vars at build time when available,
 * falling back to the API /auth/config endpoint at runtime.
 *
 * Build-time resolution eliminates a network round-trip on every page load,
 * which speeds up both production startup and E2E test execution.
 *
 * External ID handles all authentication flows:
 * - Email/password sign-in and sign-up
 * - Social login (Google, Facebook, Microsoft)
 * - Password reset
 */

import { LogLevel, BrowserCacheLocation } from '@azure/msal-browser';
import type { Configuration } from '@azure/msal-browser';

/**
 * Microsoft Entra External ID Configuration structure returned from the API
 */
export interface B2CConfig {
  instance: string;
  domain: string;
  clientId: string;
  apiScopes: string[];
  knownAuthorities: string[];
  authority?: string; // Pre-constructed authority URL from backend
}

/**
 * Auth mode configuration from API
 */
export interface AuthModeConfig {
  useAzureB2C: boolean;
  b2CConfig: B2CConfig | null;
}

/**
 * Resolves auth config from VITE build-time env vars.
 *
 * Returns null when the required vars are missing, signalling the caller
 * should fall back to the GET /auth/config API endpoint. This keeps the
 * API endpoint as a backward-compatible fallback while eliminating a
 * network round-trip when env vars are present (the common case for
 * .env.development, .env.production, and CI pipeline builds).
 */
export function getAuthConfigFromEnv(): AuthModeConfig | null {
  const useB2C = import.meta.env.VITE_USE_AZURE_B2C === 'true';
  const clientId = import.meta.env.VITE_B2C_CLIENT_ID;

  if (!useB2C || !clientId) return null;

  const instance = import.meta.env.VITE_B2C_INSTANCE || '';
  const domain = import.meta.env.VITE_B2C_DOMAIN || '';
  const tenantId = import.meta.env.VITE_B2C_TENANT_ID || '';
  const knownAuthority = import.meta.env.VITE_B2C_AUTHORITY || '';

  // Match the API's authority format: {instance}/{tenantId}/v2.0
  // MSAL uses this to construct the OpenID Connect discovery endpoint.
  // The tenant-ID format avoids an extra domain-to-tenant resolution
  // call that the domain-based format ({instance}/{domain}) requires.
  const authority = tenantId
    ? `${instance}/${tenantId}/v2.0`
    : `${instance}/${domain}`;

  return {
    useAzureB2C: true,
    b2CConfig: {
      instance,
      domain,
      clientId,
      apiScopes: (import.meta.env.VITE_B2C_API_SCOPES || '').split(' ').filter(Boolean),
      knownAuthorities: [knownAuthority],
      authority,
    },
  };
}

/**
 * Creates the MSAL configuration object for External ID
 *
 * @param b2cConfig Configuration from backend API
 * @returns MSAL Configuration object
 */
export const createMsalConfig = (b2cConfig: B2CConfig): Configuration => {
  const authority = b2cConfig.authority || `${b2cConfig.instance}/${b2cConfig.domain}`;
  const redirectUri = window.location.origin + '/auth/callback';

  // v5 moved `navigateToLoginRequestUrl` out of the global auth config and
  // into the per-call HandleRedirectPromiseOptions on handleRedirectPromise().
  // We still want the v4 behavior of NOT auto-navigating back to the original
  // request URL (B2CAuthService.processRedirectIfNeeded runs our own
  // post-login flow — sync user to backend, then React Router takes over from
  // /auth/callback). The actual flag is passed by b2cAuthService.ts when it
  // calls handleRedirectPromise; nothing to set on the global config.
  // DO NOT use `onRedirectNavigate: () => false` here — that callback fires
  // for the LOGIN redirect too, so returning false breaks the entire login
  // flow (the browser never reaches the CIAM page). We learned that the hard
  // way during the v4 -> v5 migration.
  return {
    auth: {
      clientId: b2cConfig.clientId,
      authority: authority,
      knownAuthorities: b2cConfig.knownAuthorities,
      redirectUri: redirectUri,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,

      // v5 removed `storeAuthStateInCookie` entirely. The cookie was a
      // legacy fallback for IE11 / pre-Chromium Edge where sessionStorage
      // didn't survive iframe navigation; modern browsers don't need it
      // and the option was deprecated in v4 before removal in v5.
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
          }
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  };
};

/**
 * Scopes used for login and token refresh. Do not add API scopes here.
 * In some Entra External ID setups (e.g. local accounts), requesting API
 * scopes causes AADSTS500207 ("The account type can't be used for the resource
 * you're trying to access"). We use only OIDC scopes so login works in all environments.
 * Single source of truth so apiScopes from config are never used for login.
 *
 * If you see AADSTS500207 after login (on redirect): clear site data for localhost
 * and the B2C domain (DevTools > Application > Storage > Clear site data), then
 * hard refresh and sign in again so the next authorize request uses OIDC-only scopes.
 */
export const LOGIN_SCOPES = ['openid', 'profile', 'offline_access'] as const;

/**
 * Login request configuration for External ID authentication.
 * Uses LOGIN_SCOPES only; b2cConfig.apiScopes must not be used for login.
 */
export const createLoginRequest = (_b2cConfig: B2CConfig) => {
  return {
    scopes: [...LOGIN_SCOPES],
  };
};
